/**
 * web-app/src/utils/i18nRoute.js
 *
 * EN routing:
 *   - English: /en/...
 *   - French (default): /...
 *
 * [I18N_ROUTE] Single source of truth for route prefixing.
 */

const ALLOWED = ["fr", "en"];

/**
 * Split a url-ish string into:
 * - basePath (pathname only)
 * - suffix (?query and/or #hash)
 */
const splitPathSuffix = (input) => {
    const s = String(input || "");
    const q = s.indexOf("?");
    const h = s.indexOf("#");

    let cut = -1;
    if (q !== -1 && h !== -1) cut = Math.min(q, h);
    else cut = q !== -1 ? q : h;

    if (cut === -1) return { basePath: s, suffix: "" };
    return { basePath: s.substring(0, cut), suffix: s.substring(cut) };
};

/**
 * Return "en" if pathname starts with /en or /en/
 * else "fr".
 * Accepts pathname OR pathname+search/hash safely.
 */
export const getLangFromPathname = (pathnameLike) => {
    const { basePath } = splitPathSuffix(pathnameLike);
    const p = String(basePath || "");
    return p === "/en" || p.startsWith("/en/") ? "en" : "fr";
};

/**
 * Normalize an input language to "fr" or "en".
 */
const normalizeLang = (lang) => {
    const l = String(lang || "").toLowerCase().trim();
    return ALLOWED.includes(l) ? l : "fr";
};

/**
 * Ensure string starts with "/" (except empty becomes "/").
 */
const ensureLeadingSlash = (path) => {
    const p = String(path || "");
    if (!p) return "/";
    return p.startsWith("/") ? p : `/${p}`;
};

/**
 * Remove /en prefix if present.
 * - "/en" -> "/"
 * - "/en/xxx" -> "/xxx"
 * - "/xxx" -> "/xxx"
 */
const stripEnPrefix = (path) => {
    let p = String(path || "");
    if (p === "/en") return "/";
    if (p.startsWith("/en/")) return p.substring(3); // remove "/en"
    return p;
};

/**
 * Convert a path into the localized form given a language or a pathname.
 *
 * Usage:
 * - toLocalizedPath("/bookings", "en") -> "/en/bookings"
 * - toLocalizedPath("/bookings", window.location.pathname) -> based on current URL language
 * - toLocalizedPath("/en/bookings", "fr") -> "/bookings"
 *
 * Rules:
 * - Do NOT duplicate /en
 * - For lang === "en": prefix /en (except "/" becomes "/en")
 * - For lang === "fr": remove /en prefix if present
 *
 * Supports "/path?x=1#y" (keeps ?#).
 */
export const toLocalizedPath = (path, langOrPathname) => {
    // If someone passes an absolute URL, do not alter it
    const raw = String(path || "");
    if (/^https?:\/\//i.test(raw)) return raw;

    const { basePath, suffix } = splitPathSuffix(raw);
    const input = ensureLeadingSlash(basePath);

    const inferred =
        typeof langOrPathname === "string" &&
            (String(langOrPathname).startsWith("/") || String(langOrPathname) === "/en")
            ? getLangFromPathname(langOrPathname)
            : normalizeLang(langOrPathname);

    // Remove any existing /en to avoid double prefixing
    const pure = stripEnPrefix(input);

    if (inferred === "en") {
        const localized = pure === "/" ? "/en" : `/en${pure}`;
        return `${localized}${suffix}`;
    }

    // French: no prefix
    return `${pure}${suffix}`;
};

/**
 * Wrapper to navigate while preserving language prefix.
 *
 * Example:
 *   navigateLocalized(navigate, "/bookings", window.location.pathname)
 */
export const navigateLocalized = (navigate, path, langOrPathname, options) => {
    const target = toLocalizedPath(path, langOrPathname);
    if (typeof navigate !== "function") {
        window.location.href = target;
        return;
    }
    navigate(target, options);
};